import React, { useEffect } from 'react';
import { HashRouter as Router, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import { ThemeProvider } from '@material-ui/core/styles';
import { GeneralProvider } from './Context/GeneralContext';
import { themeFactory } from '../src/themes';
import useToken from './useToken.js';
import Menu from './components/Menu/';
import MainView from './components/MainView';
import TVView from './pages/TV';
import './App.css';
import { LoginProvider } from './pages/Login/LoginContext';
import { createTheme } from '@material-ui/core/styles';

function AppContent() {
    const location = useLocation();
    const isTV = location.pathname === '/tv';

    // Verificar expiración del token al volver al foco (pantallas 24/7 que pueden suspenderse)
    useEffect(() => {
        const checkExpiry = () => {
            const stored = sessionStorage.getItem('token');
            if (!stored) return;
            const parsed = JSON.parse(stored);
            if (parsed && new Date(parsed.expiracion) < new Date()) {
                sessionStorage.removeItem('token');
                window.location.reload();
            }
        };
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') checkExpiry();
        };
        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('storage', checkExpiry);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('storage', checkExpiry);
        };
    }, []);

    return (
        <div className="app-container">
            {!isTV && <Menu />}
            <MainView />
        </div>
    );
}

// Decide qué renderizar según la ruta y el estado de autenticación.
// Debe estar dentro del Router para que useLocation() reaccione a cambios de hash.
function AppShell({ theme }) {
    const location = useLocation();
    const { token, setToken } = useToken();
    const isTV = location.pathname === '/tv';

    // La vista TV es pública — no requiere token
    if (isTV) {
        return <TVView />;
    }

    if (!token) {
        return (
            <GeneralProvider>
                <LoginProvider>
                    <Login setToken={setToken} />
                </LoginProvider>
            </GeneralProvider>
        );
    }

    const exp = JSON.parse(sessionStorage.getItem('token'));
    if (exp) {
        const diff = new Date(exp.expiracion) - new Date();
        if (diff < 0) {
            sessionStorage.removeItem('token');
        }
    }

    return (
        <GeneralProvider>
            <AppContent />
        </GeneralProvider>
    );
}

export default function App() {
    const customBreakpoints = {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1281,
            xl: 1919,
            xxl: 2560,
        },
    };

    const theme = createTheme({
        ...themeFactory(process.env.REACT_APP_THEME),
        breakpoints: customBreakpoints,
        typography: {
            fontFamily: "'WorkSans', sans-serif",
        },
    });

    document.documentElement.style.setProperty('--color-primary', theme.palette.primary.main);

    const PRIMARY_LIGHT = {
        novaTheme:    '#e7d4ff',
        tecnoTheme:   '#B8CDD6',
        colTheme:     '#B8CDD6',
        matteTheme:   '#B0C4CE',
        posTheme:     '#9FD6EF',
        skyTheme:     '#97B6FF',
        vaisandTheme: '#97AFFF',
    };
    document.documentElement.style.setProperty(
        '--color-primary-light',
        PRIMARY_LIGHT[process.env.REACT_APP_THEME] ?? '#e7d4ff'
    );

    return (
        <Router basename={process.env.REACT_APP_BASENAME || '/'}>
            <ThemeProvider theme={theme}>
                <AppShell theme={theme} />
            </ThemeProvider>
        </Router>
    );
}
